/**
 * Artifact Extraction Utilities
 *
 * Funções para parsing, validação e extração de artifacts
 */

import type { Artifact } from '../../../types/chat';

// =============================================================================
// TYPES
// =============================================================================

export interface ExtractionResult {
  cleanText: string;
  artifact: Artifact | undefined;
  isUpdate: boolean;
  originalArtifactId?: string;
}

export interface ExtractionOptions {
  messageId: number;
  existingArtifacts?: Map<string, Artifact>;
  preserveContent?: boolean;
}

// =============================================================================
// VALIDATION
// =============================================================================

const VALID_TYPES = [
  'text/markdown',
  'text/html',
  'application/vnd.ant.code',
  'application/vnd.tais.tax-comparison',
  'application/vnd.tais.tax-chart',
  'application/vnd.tais.tax-bar-chart',
  'application/vnd.tais.tax-summary',
] as const;

/**
 * Check if text contains an artifact in progress
 */
export const hasArtifactInProgress = (text: string): boolean => {
  const openTagPattern = /<\w*[Aa]rtifact\s+([^>]+)>/i;
  const hasOpenTag = openTagPattern.test(text);
  const closingTagPattern = /<\/\w*[Aa]rtifact>/i;
  const hasClosingTag = closingTagPattern.test(text);
  return hasOpenTag && !hasClosingTag;
};

/**
 * Validate artifact attributes
 */
const validateArtifactAttributes = (
  idMatch: RegExpMatchArray | null,
  typeMatch: RegExpMatchArray | null,
  titleMatch: RegExpMatchArray | null
): boolean => {
  if (!idMatch || !typeMatch || !titleMatch) {
    return false;
  }

  const idPattern = /^[a-zA-Z0-9_-]+$/;
  if (!idPattern.test(idMatch[1])) {
    return false;
  }

  const isValidType =
    VALID_TYPES.includes(typeMatch[1] as (typeof VALID_TYPES)[number]) ||
    typeMatch[1].startsWith('application/') ||
    typeMatch[1].startsWith('text/');

  return isValidType;
};

// =============================================================================
// PARSING
// =============================================================================

/**
 * Parse artifact attributes from the opening tag
 */
const parseArtifactAttributes = (attributesStr: string) => {
  const idMatch = attributesStr.match(/identifier=["']([^"']+)["']/);
  const typeMatch = attributesStr.match(/type=["']([^"']+)["']/);
  const titleMatch = attributesStr.match(/title=["']([^"']+)["']/);
  const languageMatch = attributesStr.match(/language=["']([^"']+)["']/);

  return { idMatch, typeMatch, titleMatch, languageMatch };
};

/**
 * Extract artifact content between opening and closing tags
 */
const extractArtifactContent = (
  text: string,
  openMatch: RegExpMatchArray,
  startIdx: number
): { content: string; endIdx: number; fullMatch: string } | null => {
  const closingTagPattern = /<\/\w*[Aa]rtifact>/i;
  const remainingText = text.substring(startIdx);
  const closingMatch = remainingText.match(closingTagPattern);

  if (!closingMatch || closingMatch.index === undefined) {
    const content = remainingText.trim();
    const endIdx = text.length;
    const fullMatch = text.substring(openMatch.index!);
    return { content, endIdx, fullMatch };
  }

  const endIdx = startIdx + closingMatch.index;
  const content = text.substring(startIdx, endIdx).trim();
  const fullMatchEnd = endIdx + closingMatch[0].length;
  const fullMatch = text.substring(openMatch.index!, fullMatchEnd);

  return { content, endIdx, fullMatch };
};

/**
 * Create artifact object from parsed data
 */
const createArtifact = (
  id: string,
  type: string,
  title: string,
  content: string,
  language: string | undefined,
  messageId: number,
  existingArtifact?: Artifact
): Artifact => {
  const now = new Date().toISOString();

  return {
    id,
    type,
    title,
    content,
    language,
    messageId,
    createdAt: existingArtifact?.createdAt || now,
    updatedAt: existingArtifact ? now : undefined,
    isUpdating: !!existingArtifact,
    previousVersion: existingArtifact?.content,
  };
};

// =============================================================================
// EXTRACTION
// =============================================================================

/**
 * Extract artifact from text
 */
export const extractArtifact = (
  text: string,
  options: ExtractionOptions
): ExtractionResult => {
  const { messageId, existingArtifacts = new Map() } = options;

  const openTagPattern = /<\w*[Aa]rtifact\s+([^>]+)>/i;
  const openMatch = text.match(openTagPattern);

  if (!openMatch) {
    return { cleanText: text, artifact: undefined, isUpdate: false };
  }

  const attributesStr = openMatch[1];
  const { idMatch, typeMatch, titleMatch, languageMatch } =
    parseArtifactAttributes(attributesStr);

  if (!validateArtifactAttributes(idMatch, typeMatch, titleMatch)) {
    return { cleanText: text, artifact: undefined, isUpdate: false };
  }

  const id = idMatch![1];
  const type = typeMatch![1];
  const title = titleMatch![1];
  const language = languageMatch ? languageMatch[1] : undefined;

  const startIdx = text.indexOf('>', openMatch.index!) + 1;
  const contentData = extractArtifactContent(text, openMatch, startIdx);

  if (!contentData) {
    return { cleanText: text, artifact: undefined, isUpdate: false };
  }

  const { content, fullMatch } = contentData;

  const existingArtifact = existingArtifacts.get(id);
  const isUpdate = !!existingArtifact;

  const artifact = createArtifact(
    id,
    type,
    title,
    content,
    language,
    messageId,
    existingArtifact
  );

  let cleanText = text.replace(fullMatch, '').trim();

  // Remove duplicate JSON content for DOCX structured artifacts
  if (artifact && artifact.type === 'application/vnd.tais.docx-structured') {
    const jsonPattern = /\{[\s\S]*?"sections"[\s\S]*?\}(?=\s*$|[\s\n]*<)/;
    const jsonMatch = cleanText.match(jsonPattern);

    if (jsonMatch) {
      const matchSample = jsonMatch[0].substring(0, 200).replace(/\s+/g, '');
      const artifactSample = artifact.content
        .substring(0, 200)
        .replace(/\s+/g, '');

      if (
        matchSample.includes(artifactSample.substring(0, 50)) ||
        artifactSample.includes(matchSample.substring(0, 50))
      ) {
        cleanText = cleanText.replace(jsonMatch[0], '').trim();
      }
    }
  }

  return {
    cleanText,
    artifact,
    isUpdate,
    originalArtifactId: existingArtifact?.id,
  };
};

/**
 * Extract multiple artifacts from text
 */
export const extractMultipleArtifacts = (
  text: string,
  options: ExtractionOptions
): { cleanText: string; artifacts: Artifact[]; updates: string[] } => {
  let remainingText = text;
  const artifacts: Artifact[] = [];
  const updates: string[] = [];
  const { existingArtifacts = new Map() } = options;

  while (true) {
    const result = extractArtifact(remainingText, {
      ...options,
      existingArtifacts,
    });

    if (!result.artifact) {
      break;
    }

    artifacts.push(result.artifact);

    if (result.isUpdate) {
      updates.push(result.artifact.id);
    }

    remainingText = result.cleanText;
    existingArtifacts.set(result.artifact.id, result.artifact);
  }

  return {
    cleanText: remainingText,
    artifacts,
    updates,
  };
};

// =============================================================================
// CHANGE DETECTION
// =============================================================================

/**
 * Check if artifact content has meaningful changes
 */
export const hasSignificantChanges = (
  newContent: string,
  oldContent: string,
  threshold: number = 0.1
): boolean => {
  if (!oldContent) return true;

  const newLines = newContent.split('\n').filter((line) => line.trim());
  const oldLines = oldContent.split('\n').filter((line) => line.trim());

  const maxLines = Math.max(newLines.length, oldLines.length);
  if (maxLines === 0) return false;

  let changedLines = 0;
  const minLength = Math.min(newLines.length, oldLines.length);

  for (let i = 0; i < minLength; i++) {
    if (newLines[i] !== oldLines[i]) {
      changedLines++;
    }
  }

  changedLines += Math.abs(newLines.length - oldLines.length);

  const changeRatio = changedLines / maxLines;
  return changeRatio > threshold;
};

/**
 * Generate update summary for artifact changes
 */
export const generateUpdateSummary = (
  oldArtifact: Artifact,
  newArtifact: Artifact
): string => {
  const changes: string[] = [];

  if (oldArtifact.title !== newArtifact.title) {
    changes.push(
      `título alterado de "${oldArtifact.title}" para "${newArtifact.title}"`
    );
  }

  if (oldArtifact.type !== newArtifact.type) {
    changes.push(
      `tipo alterado de "${oldArtifact.type}" para "${newArtifact.type}"`
    );
  }

  if (oldArtifact.content !== newArtifact.content) {
    const hasSignificant = hasSignificantChanges(
      newArtifact.content,
      oldArtifact.content
    );
    if (hasSignificant) {
      changes.push('conteúdo atualizado');
    } else {
      changes.push('conteúdo ajustado');
    }
  }

  if (changes.length === 0) {
    return 'artefato atualizado';
  }

  return `Artefato atualizado: ${changes.join(', ')}`;
};
